import {
  OverviewCardProps,
  OverviewCardTypes,
  SourceDataRequestTypes
} from '../../shared/OverviewCard';
import axios from 'axios';

export type RequestItem = {
  id: number;
  request_type: OverviewCardTypes;
  name?: string;
  description: string;
  created_at: string;
  additionalInfo: string;
  is_open?: boolean;
};

export interface QuestionnaireRequest extends RequestItem {
  title: string;
  body: {
    questions: any[];
    answers: any;
  };
  completed: boolean;
  assigned_person_name: string;
}

export interface DocumentRequest extends RequestItem {
  status: string;
  fulfilled_document_types: number;
  rejected_document_types: number;
  warning_message: string;
  total_document_types: number;
  files_count: number;
}

export enum SignStatus {
  OPEN = 'open',
  SIGNED = 'signed'
}

export interface SignRequest extends RequestItem {
  organization_sign_request_id: number;
  sign_type: string;
  sign_status: SignStatus;
  signed_by: string;
  assignable_person_name: string;
}

export interface SourceData extends RequestItem {
  completed: boolean;
  source_type: SourceDataRequestTypes;
  assigned_person_name: string;
}

export interface FinlyForm extends RequestItem {
  completed: boolean;
}

export type DataResponse = {
  requests: RequestItem[];
};

export async function fetchRequestsList(filterByOpen?: boolean): Promise<RequestItem[]> {
  const {
    data: { requests = [] }
  } = await axios.get<DataResponse>('/request');

  return filterByOpen ? requests.filter(({ is_open }) => is_open) : requests;
}

export function transformOverviewCardProps(
  requestItem: RequestItem
): OverviewCardProps {
  const defaultProps = {
    id: requestItem.id,
    title: requestItem.name,
    createdAt: requestItem.created_at,
    completed: false,
    currentAmount: 0,
    totalAmount: 0,
    type: OverviewCardTypes.DOCUMENT,
    additionalInfo: ''
  };

  if (requestItem.request_type === OverviewCardTypes.DOCUMENT) {
    const item = requestItem as DocumentRequest;
    return {
      ...defaultProps,
      disabled: false,
      currentAmount: item.fulfilled_document_types,
      totalAmount: item.total_document_types,
      rejectedDocuments: item.rejected_document_types,
      warningMessage: item.warning_message
    };
  }

  if (requestItem.request_type === OverviewCardTypes.SIGNING) {
    const item = requestItem as SignRequest;
    return {
      ...defaultProps,
      completed: item.sign_status === SignStatus.SIGNED,
      currentAmount: item.sign_status === SignStatus.SIGNED ? 1 : 0,
      totalAmount: 1,
      type: OverviewCardTypes.SIGNING,
      additionalInfo: item.assignable_person_name
    };
  }

  if (requestItem.request_type === OverviewCardTypes.QUESTIONNAIRE) {
    const item = requestItem as QuestionnaireRequest;
    const pageAmount = item.body.questions.reduce(
      (total, parentBlock) =>
        parentBlock.formType === 'page' ? total + 1 : total,
      0
    );
    return {
      ...defaultProps,
      completed: item.completed,
      disabled: item.completed,
      currentAmount: pageAmount
        ? item.completed
          ? pageAmount
          : item.body?.answers?.pageIndex || 0
        : '?',
      totalAmount: pageAmount || '?',
      type: OverviewCardTypes.QUESTIONNAIRE,
      title: item.title,
      additionalInfo: item.assigned_person_name
    };
  }

  if (requestItem.request_type === OverviewCardTypes.SOURCE_DATA) {
    const item = requestItem as SourceData;
    return {
      ...defaultProps,
      type: OverviewCardTypes.SOURCE_DATA,
      currentAmount: item.completed ? 1 : 0,
      totalAmount: 1,
      sourceDataRequestType: item.source_type,
      additionalInfo: item.assigned_person_name
    };
  }

  if (requestItem.request_type === OverviewCardTypes.FINLY_FORM) {
    const item = requestItem as FinlyForm;
    return {
      ...defaultProps,
      type: OverviewCardTypes.FINLY_FORM,
      currentAmount: item.completed ? 1 : 0,
      totalAmount: 1
    };
  }

  return defaultProps;
}

export function getRequestItemKey(requestItem: RequestItem): string {
  return `${requestItem.request_type}_${requestItem.id}`;
}
